import { Injectable, inject, signal } from '@angular/core';
import { ToastService } from './toast.service';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { environment } from '../../../environments/environment';
import { isTauri } from '../tauri-runtime';

/**
 * UpdaterService — desktop (Tauri) in-app updates.
 *
 * RESPONSIBILITY: Check the configured update endpoint and, when the user
 * confirms, download + install a newer build and relaunch. All heavy lifting
 * (signature verification, download, install, relaunch) happens in Rust via
 * the `check_for_update` / `install_update` commands (src-tauri/src/lib.rs);
 * this service is a thin, analytics-aware front-end.
 *
 * SIGNALS:
 *   available  — metadata for a pending update, or null
 *   checking   — a check is in flight
 *   installing — a download/install is in flight
 *
 * USED BY: AppComponent (silent boot check) + the native "Check for updates"
 *   menu item (menu:check-updates → interactive check).
 *
 * AI CONTEXT:
 *   - No-op outside Tauri; safe to call unconditionally.
 *   - Inert until the updater is configured (plugins.updater pubkey/endpoint
 *     in tauri.conf.json + a signed release). When unconfigured the Rust
 *     command returns an error which we treat as "couldn't check" — silent on
 *     the boot path, a gentle toast on the interactive path.
 *   - Uses raw `invoke` (no @tauri-apps/plugin-updater npm dep) so the web
 *     bundle pulls in nothing extra.
 */

interface UpdateMeta {
  version: string;
  current_version: string;
  notes: string | null;
}

/** Extract a safe message from an unknown error without serialising stack
 *  frames or internal paths into the console. */
function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

@Injectable({ providedIn: 'root' })
export class UpdaterService {
  private readonly toast = inject(ToastService);
  private readonly analytics = inject(AnalyticsService);

  private readonly _available = signal<UpdateMeta | null>(null);
  /** Metadata for a pending update, or null. */
  readonly available = this._available.asReadonly();
  private readonly _checking = signal(false);
  /** A version check is in flight. */
  readonly checking = this._checking.asReadonly();
  private readonly _installing = signal(false);
  /** A download/install is in flight. */
  readonly installing = this._installing.asReadonly();

  /**
   * Check for an update. `source` distinguishes the silent boot check from
   * the user-initiated menu check (which surfaces "you're up to date" and
   * errors; the boot check stays silent).
   */
  async check(source: 'boot' | 'menu'): Promise<void> {
    if (!isTauri || this.checking() || this.installing()) return;
    this._checking.set(true);
    const interactive = source === 'menu';
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const meta = await invoke<UpdateMeta | null>('check_for_update');
      if (meta) {
        this._available.set(meta);
        this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_UPDATE_CHECKED, {
          source,
          outcome: 'update-available',
          current_version: meta.current_version,
          available_version: meta.version,
        });
        this.promptInstall(meta, interactive);
      } else {
        this._available.set(null);
        this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_UPDATE_CHECKED, {
          source,
          outcome: 'up-to-date',
          current_version: environment.APP_VERSION,
        });
        if (interactive) {
          this.toast.show({
            id: 'updater-up-to-date',
            title: 'You’re up to date',
            body: 'RICOH Workplace is running the latest version.',
            severity: 'info',
            priority: 'high',
          });
        }
      }
    } catch (err) {
      // Unconfigured updater or network/endpoint failure. Silent on boot.
      // Log only the message (not the raw error) to avoid leaking internal
      // file paths / stack frames into the console (security audit P3).
      console.warn('[UpdaterService] check failed:', errMessage(err));
      this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_UPDATE_CHECKED, {
        source,
        outcome: 'error',
        current_version: environment.APP_VERSION,
      });
      if (interactive) {
        // An unpublished feed (no release yet, placeholder key) reads
        // differently from a network blip: say which one it is.
        const notConfigured = /status code|release JSON|not found|404/i.test(errMessage(err));
        this.toast.show({
          id: 'updater-check-failed',
          title: notConfigured ? 'Updates aren’t set up for this build' : 'Couldn’t check for updates',
          body: notConfigured
            ? 'No release feed is published yet, so this build cannot update itself. Install a newer build by hand when one is shared.'
            : 'Please try again later.',
          severity: 'warning',
          priority: 'high',
        });
      }
    } finally {
      this._checking.set(false);
    }
  }

  /**
   * Download + install the pending update, then relaunch. The Rust command
   * relaunches the app on success, so this normally never resolves.
   */
  async install(): Promise<void> {
    const meta = this.available();
    if (!isTauri || !meta || this.installing()) return;
    this._installing.set(true);
    this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_UPDATE_INSTALL_STARTED, {
      from_version: meta.current_version,
      to_version: meta.version,
    });
    this.toast.show({
      id: 'updater-installing',
      title: `Updating to ${meta.version}…`,
      body: 'The app will restart when the update is ready.',
      severity: 'info',
      priority: 'high',
    });
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('install_update');
      // On success the app relaunches and we never reach here.
    } catch (err) {
      console.warn('[UpdaterService] install failed:', errMessage(err));
      this._installing.set(false);
      this.toast.show({
        id: 'updater-install-failed',
        title: 'Update failed',
        body: 'The update couldn’t be installed. Please try again later.',
        severity: 'error',
        priority: 'high',
      });
    }
  }

  /**
   * Surface the available update. On the interactive (menu) path we proceed to
   * install immediately — the user explicitly asked. On the boot path we leave
   * a non-intrusive toast so the user can install via the menu when ready.
   */
  private promptInstall(meta: UpdateMeta, interactive: boolean): void {
    if (interactive) {
      void this.install();
      return;
    }
    this.toast.show({
      id: 'updater-available',
      title: `Update available · ${meta.version}`,
      body: 'Open the menu → Check for updates to install.',
      severity: 'info',
      priority: 'high',
    });
  }
}
